<script lang="ts">
import { defineComponent, toRefs } from 'vue'
import { useHead } from '@vueuse/head'

import BackgroundWrapper from '@base/components/BackgroundWrapper/BackgroundWrapper.vue'
import SplitContent from '@player/_components/SplitContent/SplitContent.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USImage from '@base/components/USImage/USImage.vue'

import { playerTitle } from '@player/_lib/playerTitle'
import { staticRouteNames } from '@player/static/router/routeNames'
import { usersRouteNames } from '@player/users/router/routeNames'
import { useUserStore } from '@base/store/userStore'
import UpSwingLogo from '@base/components/UpSwingLogo/UpSwingLogo.vue'

import heroImage from '@player/static/assets/about-header.jpg'
import twoFriends from '@player/static/assets/about-two-friends.png'
import aboutBg from '@player/static/assets/about-bg.svg'
import { Background } from '@base/_types/Background'
import albertaGolfImage from '@player/static/assets/alberta-golf.png'

export default defineComponent({
  name: 'About',
  components: {
    USHeroBanner,
    USImage,
    USButton,
    SplitContent,
    BackgroundWrapper,
    UpSwingLogo
  },
  setup() {
    const userStore = toRefs(useUserStore())
    const { isAuth } = userStore

    useHead({
      title: playerTitle('About')
    })

    const bgImage: Background = {
      fullWidth: true,
      overflow: true,
      media: [
        {
          type: 'image',
          position: {
            alignHorizontal: 'right_50',
            alignVertical: 'top_50',
            centerAnchor: false,
            cover: false,
            foreground: false,
            objectPositionHorizontal: 'center',
            objectPositionVertical: 'center'
          },
          imageSet: {
            mobile: {
              fadeIn: true,
              lazy: true,
              src: aboutBg
            }
          }
        }
      ]
    }

    return {
      heroImage,
      twoFriends,
      aboutBg,
      staticRouteNames,
      usersRouteNames,
      isAuth,
      bgImage,
      albertaGolfImage
    }
  }
})
</script>

<template>
  <USHeroBanner class="about__hero-banner">
    <USImage
      :src="heroImage"
      class="about__hero-banner--img"
      alt="About Hero Image"
      object-fit="cover"
      object-position="80% 20%"
    />
  </USHeroBanner>
  <section>
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <h1 class="about__heading heading--h3">
          Whether you want to improve your game or simply looking to diversify your recreational playing options, UpSwing is here to help.
        </h1>
      </div>
    </div>
  </section>
  <BackgroundWrapper :background="bgImage" />
  <SplitContent>
    <template #left>
      <USImage
        :src="twoFriends"
        alt="Two Friends Image"
        object-fit="initial"
        aspect-ratio="none"
        class="about__img"
      />
    </template>
    <template #right>
      <div class="about__content body--p1--regular">
        <div class="grid__row margin-bottom--6 ">
          <div class="grid__column grid__column--offset--2 grid__column--8 grid__column--breakpoint--7--offset--0 grid__column--breakpoint--7--8">
          <UpSwingLogo class="test"/>
        </div>
        <div class="grid__column grid__column--offset--4 grid__column--4 grid__column--breakpoint--7--offset--1 grid__column--breakpoint--7--3">
          <USImage
            :src="albertaGolfImage"
            alt="Alberta Golf Logo"
            object-fit="initial"
            aspect-ratio="none"
          />
        </div>
        </div>
        <p>
          We are the recreational brand of 
          <a
            href="https://albertagolf.org/"
            target="_blank"
            class="body--hyperlink"
          >
            <span>Alberta Golf</span>
          </a>
          , anchored with a digital platform designed and built to support the golf industry and accommodate recreational players looking to diversify their playing options. The UpSwing brand supports the largest segment of Alberta's golf market (675,000 golfers) by showcasing courses, events and playing opportunities.
        </p>
      </div>
    </template>
  </SplitContent>
  <section class="about__prefooter">
    <div class="grid__row">
      <div
        class="grid__column grid__column--12 grid__column--breakpoint--7--10 grid__column--breakpoint--7--offset--1"
      >
        <h2 class="about__signup display--d1">
          Find your fun.
        </h2>
      </div>
    </div>
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <router-link
          v-if="!isAuth"
          :to="{ name: usersRouteNames.Register }"
          class="about__signup--btn"
        >
          <USButton label="Join UpSwing" />
        </router-link>
        <router-link
          v-else
          class="about__signup--btn"
          :to="{ name: staticRouteNames.Home }"
        >
          <USButton label="Join UpSwing" />
        </router-link>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.about {
  color: var(--color--neutral-a1);

  &__hero-banner {
    margin-top: calc(-1 * var(--spacing--5));
  }

  &__hero-banner--img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &__heading {
    padding-bottom: var(--spacing--5);
    padding-top: var(--spacing--3);
    text-align: center;
  }

  &__bg {
    position: absolute;
    right: 0;
    top: 0;
    width: 140%;
    z-index: -100;
  }

  &__content {
    padding-top: var(--spacing--4);
  }

  &__prefooter {
    margin-bottom: var(--spacing--template);
    margin-top: var(--spacing--template);
    text-align: center;
  }

  &__signup {
    margin-top: var(--spacing--5);
    margin-bottom: var(--spacing--5);
    color: var(--color--secondary-c3);
  }

  &__contact-btn {
    margin-left: var(--spacing--5);
  }
}
</style>
